/**
 * The merchant's brand identity as a theme consumes it. It is stored in `tenants.settings.brand`
 * and arrives inside the untyped settings bag, so it is narrowed here once rather than in every
 * theme that draws a header.
 */

/** The logo, mark and printable name a theme draws its identity with; empty strings mean "not set yet". */
export type StoreBrand = {
  /** What to print: the brand name when the merchant set one, otherwise the store's own name. */
  name: string;
  logoUrl: string;
  markUrl: string;
  /** Whether a real logo image exists, so a header can choose a wordmark instead of an empty slot. */
  hasLogo: boolean;
};

/** The brand sub-object as it sits in the settings jsonb; every key is absent until first saved. */
type RawBrand = { name?: unknown; logoUrl?: unknown; markUrl?: unknown };

// Reads one brand string out of the untyped settings bag, answering '' for anything that is not one.
const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

// Narrows the tenant's settings bag into the brand a theme draws, falling back to the store's own name.
export function readBrand(settings: Record<string, unknown>, storeName: string): StoreBrand {
  const raw = (settings.brand ?? {}) as RawBrand;
  const logoUrl = text(raw.logoUrl);
  return {
    name: text(raw.name) || storeName,
    logoUrl,
    markUrl: text(raw.markUrl),
    hasLogo: logoUrl !== ''
  };
}

// Reads the brand out of the store's settings answer, where it sits inside `settings`; reading it off the answer
// itself found nothing, so a store's uploaded logo and icon never reached its storefront.
export const storeBrandOf = (answer: { settings?: Record<string, unknown> | null } | null, storeName: string): StoreBrand =>
  readBrand(answer?.settings ?? {}, storeName);

// Names the store on its own pages: a demo store wears the name of the theme it shows, as each Shopify theme's demo is named for it.
export const shownStoreName = (settings: Record<string, unknown> | null | undefined, storeName: string, themeName: string | undefined): string =>
  settings?.isDemoStore === true && themeName ? themeName : storeName;
